/**
 * @param  {Function} fn
 * 有使用location.href 跳转的，延迟一小段时间进行跳转，防止请求canceled
 */
import Cookies from "js-cookie";

export function doAfterTimeout(fn: Function) {
  setTimeout(() => {
    fn();
  }, 300);
}

/**
 * @param  {{[key:string]:string}} query
 * @param  {{[key:string]:string}} extendQuery
 */
export function mergeQuery(
  query: Record<string, string>,
  extendQuery: Record<string, string>
) {
  const finalQuery = { ...query, ...extendQuery };
  const queryString = Object.keys(finalQuery)
    .map(key => `${key}=${finalQuery[key]}`)
    .join("&");
  return queryString ? "?" + queryString : "";
}

/**
 * @param  {boolean} isShow
 */
export function preventBodyScroll(isShow: boolean): void {
  const html = document.documentElement;
  const body = document.body;
  if (isShow) {
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
  } else {
    body.style.overflow = "auto";
    html.style.overflow = "auto";
  }
}

// 轮询
export const polling = (
  fn: (sum: number, max: number) => boolean,
  timeout?: number,
  max?: number
) => {
  const _timeout = timeout || 300;
  const _max = max || 5000;
  let sum = 0;
  const timer = window.setInterval(() => {
    const isSuccess = fn(sum, _max);
    if (isSuccess) {
      window.clearInterval(timer);
    } else {
      sum += _timeout;
    }
  }, _timeout);
  return timer;
};

// 设置cookie要使用这个方法，因为iframe形式嵌入时需要添加 { secure: true, sameSite: "None" }:
export const setCookie = (name: string, value: string) => {
  Cookies.set(name, value, { secure: true, sameSite: "None" });
};

export const wait = <T = undefined>(time: number = 0, val?: T): Promise<T> =>
  new Promise(resolve => {
    setTimeout(() => resolve(val as T), time);
  });

/**
 * 一些promise任务要考虑超时，此处做通用处理
 * @param promise 要执行的promise
 * @param defaultVal 执行函数如果超时，要resolve的默认值
 * @param time 超时时间
 */
export const racePromise = <K extends any>(
  promise: Promise<K>,
  time: number,
  defaultVal: K
): Promise<K> => {
  if (!time) return promise;

  return Promise.race([promise, wait(time, defaultVal)]);
};

/**
 * 生产环境下一些promise任务要考虑超时，此处做通用处理
 * @param promise 要执行的promise
 * @param defaultVal 执行函数如果超时，要resolve的默认值
 * @param time 超时时间
 */
export const raceIfProd = <K extends any>(
  promise: Promise<K>,
  time: number,
  defaultVal: K
): Promise<K> => {
  return racePromise(promise, IS_PROD ? time : 0, defaultVal);
};

 /**
  * JSON 转换为 queryString
  * @param  {Record<string, string>} obj
  * @param  {string} prefix?
  */
 export const jsonToQuery = (obj: Record<string, string>, prefix?: string) => (typeof prefix === 'undefined' ? '?' : prefix) +
 Object.keys(obj)
   .filter(item => typeof obj[item] !== 'undefined' && obj[item] !== null)
   .map(item => [item, encodeURIComponent(obj[item])].join('='))
   .join('&');

/**
* queryString 转化为 JSON 对象
* @param url
* @returns {{}}
*/
export const queryToJson = (url?: string) => {
 const targetUrl = url || window.location.search || '';
 const params = targetUrl.slice(targetUrl.indexOf('?') + 1);
 const obj: Record<string, string> = {};
 params.split('&').forEach((item) => {
   const row = item.split('=');
   if (row.length === 2) {
     obj[row[0]] = decodeURIComponent(row[1]);
   }
 });
 return obj;
};

/**
 * 页面无刷新情况下更新 url query
 * @param queryObj 要附加的 query 对象
 */
export const replaceQueryNoRefresh = (queryObj: Record<string, string>) => {
  if(!window) return

  window.history.replaceState(null, '', `${window.location.pathname}${jsonToQuery({
    ...queryToJson(),
    ...queryObj
  })}${location.hash}`);
}

/**
 * 移除一个 url 中指定 query参数，返回新 query
 * @param {Array} props 需要清除字段
 */
 export const removeQueryGetNewQuery = (removeKeyArr : string[] = []) => {
  const curQuery = queryToJson();
  const newQuery: Record<string, string> = {}
  Object.keys(curQuery).forEach(key => {
    if(!removeKeyArr.includes(key)) {
      newQuery[key] = curQuery[key]
    }}
  )

  return jsonToQuery(newQuery)
};

/**
 * 移除一个 url 中指定 query参数，返回新 url
 * @param {Array} props 需要清除字段
 */
 export const removeQuery = (removeKeyArr : string[] = []) => {
  const newQuery =  removeQueryGetNewQuery(removeKeyArr)
  const {origin, pathname, hash}  = location

  return `${origin}${pathname}${newQuery}${hash}`
};

/**
 * 不刷新页面的清除 url 参数
 * @param {Array} props 需要清除字段
 */
 export const removeQueryNoRefresh = (removeKeyArr : string[] = []) => {
  const newQuery =  removeQueryGetNewQuery(removeKeyArr)
  const {pathname, hash}  = location

  window.history.replaceState(null, '', `${pathname}${newQuery}${hash}`);
};

/**
 * 替换字符串里的占位符
 * @param str 要替换的字符串
 * @param obj object 键值对
 * @returns 
 */
export function parseTemplate(str: string, obj: Record<string, string>) {
  return str.replace(/\{\{\{([^}]*)\}\}\}/g, key => {
    const k = key.replace(/\{|\}/g, '')
    return obj?.[k]|| ''
  })
}

const DEC: Record<string, string> = {
  '-': '+',
  '_': '/',
  '.': '='
}

export function decodeAtoB (safe: string) {
  return (safe || '').replace(/[-_.]/g, (m) => DEC[m])
}

